import React, { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { TailSpin } from 'react-loader-spinner'
import Modal from 'react-modal'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import Checkbox from '../../../../shared/components/Checkbox/Checkbox'
import Input from '../../../../shared/components/Input/Input'
import SuccessModal from '../../../../shared/components/SuccessModal/SuccessModal'
import { pushToGTM } from '../../../../shared/utis/gtm'
import { submitToZohoReport } from '../../../../utils/api'
import styles from './styles.module.scss'

export const REPORT_DOWNLOAD_MODAL = 'report-download'

const ReportDownloadModal = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isModalOpen = searchParams.get('modal') === REPORT_DOWNLOAD_MODAL
  const [isSuccessOpen, setIsSuccessOpen] = useState(false)
  const [isLoad, setIsLoad] = useState(false)

  const {
    register,
    formState: { errors },
    reset,
    handleSubmit,
    getValues,
  } = useForm()

  const closeModal = useCallback(() => {
    const params = new URLSearchParams(location.search)
    params.delete('modal')
    navigate(
      { pathname: location.pathname, search: params.toString(), hash: location.hash },
      { replace: true }
    )
  }, [location, navigate])

  const onSubmit = async () => {
    setIsLoad(true)
    const firstName = getValues('firstName')
    const lastName = getValues('lastName')
    const email = getValues('mail')
    const organisation = getValues('organisation')
    const industry = getValues('industry')
    const description = industry ? `Industry: ${industry}` : undefined

    try {
      await submitToZohoReport({
        company: organisation,
        firstName,
        lastName,
        ...(email ? { email } : {}),
        ...(description ? { description } : {}),
        onBeforeSuccess: () => {
          reset()
          closeModal()
          window.open('/report/central-asia-caspian-2026', '_blank', 'noopener,noreferrer')
          setIsSuccessOpen(true)
        },
      })
    } finally {
      setIsLoad(false)
    }
  }

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        appElement={document.getElementById('root') ?? undefined}
        contentLabel="Download Central Asia & Caspian report"
        bodyOpenClassName={styles.modalBodyOpen}
        className={styles.reportDrawerContent}
        overlayClassName={styles.reportDrawerOverlay}
        closeTimeoutMS={300}
      >
        <div className={styles.drawerWrapper}>
          <div className={styles.drawerLeftColumn}>
            <img
              className={styles.reportImage}
              src={require('../../../../../public/images/urus-web-report-mockup-1.png')}
              alt="Central Asia & Caspian report cover"
            />
            <div className={`${styles.drawerIntro} ${styles.drawer_content_container}`}>
              <div className={styles.drawerIntroContent}>
                <p className={styles.drawerIntroText}>
                  This report provides a clear-eyed analysis of one of Eurasia's most strategically
                  important regions, combining country-level spotlights with cross-border themes
                  shaping trade, investment, and geopolitics in 2025 and beyond.
                </p>
                <p className={styles.drawerIntroText}>
                  It covers key markets across Central Asia and the South Caucasus, alongside
                  regional dynamics including hydrocarbons and critical raw materials,
                  sanctions-driven trade shifts, capital markets, digital transformation, and
                  great-power competition.
                </p>
              </div>
            </div>
            <div className={styles.drawerFindInside}>
              <div className={styles.drawer_content_container}>
                <div className={styles.drawerFindInsideContent}>
                  <h4 className={styles.drawerFindInsideTitle}>What you'll find inside:</h4>
                  <div className={styles.drawerFindInsideGrid}>
                    <div className={styles.drawerFindInsideItem}>
                      <span className={styles.drawerFindInsideCircle} aria-hidden />
                      <div className={styles.drawerFindInsideBody}>
                        <strong className={styles.drawerFindInsideItemTitle}>
                          Country-level spotlights (2025)
                        </strong>
                        <p className={styles.drawerFindInsideItemText}>
                          Latest economic indicators and market developments across Central Asia and
                          <br />
                          the South Caucasus
                        </p>
                      </div>
                    </div>
                    <div className={styles.drawerFindInsideItem}>
                      <span className={styles.drawerFindInsideCircle} aria-hidden />
                      <div className={styles.drawerFindInsideBody}>
                        <strong className={styles.drawerFindInsideItemTitle}>
                          Hydrocarbons and critical minerals
                        </strong>
                        <p className={styles.drawerFindInsideItemText}>
                          The region's energy and raw materials advantage
                        </p>
                      </div>
                    </div>
                    <div className={styles.drawerFindInsideItem}>
                      <span className={styles.drawerFindInsideCircle} aria-hidden />
                      <div className={styles.drawerFindInsideBody}>
                        <strong className={styles.drawerFindInsideItemTitle}>
                          Debt and equity markets
                        </strong>
                        <p className={styles.drawerFindInsideItemText}>
                          Central Asia's maturing financial landscape
                        </p>
                      </div>
                    </div>
                    <div className={styles.drawerFindInsideItem}>
                      <span className={styles.drawerFindInsideCircle} aria-hidden />
                      <div className={styles.drawerFindInsideBody}>
                        <strong className={styles.drawerFindInsideItemTitle}>
                          Digital and fintech economies
                        </strong>
                        <p className={styles.drawerFindInsideItemText}>
                          Emerging tech hubs and new growth drivers
                        </p>
                      </div>
                    </div>
                    <div className={styles.drawerFindInsideItem}>
                      <span className={styles.drawerFindInsideCircle} aria-hidden />
                      <div className={styles.drawerFindInsideBody}>
                        <strong className={styles.drawerFindInsideItemTitle}>
                          Sanctions and supply chains
                        </strong>
                        <p className={styles.drawerFindInsideItemText}>
                          How trade routes and commercial flows
                          <br />
                          are being reshaped
                        </p>
                      </div>
                    </div>
                    <div className={styles.drawerFindInsideItem}>
                      <span className={styles.drawerFindInsideCircle} aria-hidden />
                      <div className={styles.drawerFindInsideBody}>
                        <strong className={styles.drawerFindInsideItemTitle}>
                          China, the Gulf, and the US
                        </strong>
                        <p className={styles.drawerFindInsideItemText}>
                          The new great-power competition in Central Asia and the Caucasus
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.drawerInner}>
            <button
              type="button"
              className={styles.drawerClose}
              onClick={closeModal}
              aria-label="Close report form"
            >
              ×
            </button>
            <div className={styles.drawerContent}>
              <h3 className={styles.drawerTitle}>Central Asia & Caspian</h3>
              <p className={styles.drawerSubtitle}>
                2025 TRENDS <span className={styles.drawerSubtitleSeparator}>/</span> 2026
                TRAJECTORIES
              </p>
              <form
                className={styles.drawerForm}
                onSubmit={(e) => {
                  e.preventDefault()
                  void handleSubmit(onSubmit)()
                }}
              >
                <div className={styles.drawerField}>
                  <Input
                    name="firstName"
                    register={register}
                    required
                    rest={{
                      disabled: isLoad,
                      className:
                        `${styles.drawerInput} ${errors.firstName ? styles.drawerInputError : ''}`.trim(),
                      placeholder: 'First name',
                      'aria-label': 'First name',
                    }}
                  />
                  {errors.firstName && (
                    <span className={styles.drawerError} role="alert">
                      {(errors.firstName.message as string) || 'First name is required'}
                    </span>
                  )}
                </div>
                <div className={styles.drawerField}>
                  <Input
                    name="lastName"
                    register={register}
                    rest={{
                      disabled: isLoad,
                      className:
                        `${styles.drawerInput} ${errors.lastName ? styles.drawerInputError : ''}`.trim(),
                      placeholder: 'Last name',
                      'aria-label': 'Last name',
                    }}
                  />
                  {errors.lastName && (
                    <span className={styles.drawerError} role="alert">
                      {(errors.lastName.message as string) || 'Last name is required'}
                    </span>
                  )}
                </div>
                <div className={styles.drawerField}>
                  <Input
                    name="mail"
                    register={register}
                    pattern={{
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    }}
                    rest={{
                      disabled: isLoad,
                      className:
                        `${styles.drawerInput} ${errors.mail ? styles.drawerInputError : ''}`.trim(),
                      placeholder: 'Company Email',
                      'aria-label': 'Company Email',
                    }}
                  />
                  {errors.mail && (
                    <span className={styles.drawerError} role="alert">
                      {(errors.mail.message as string) || 'Valid email is required'}
                    </span>
                  )}
                </div>
                <div className={styles.drawerField}>
                  <Input
                    name="organisation"
                    register={register}
                    rest={{
                      disabled: isLoad,
                      className:
                        `${styles.drawerInput} ${errors.organisation ? styles.drawerInputError : ''}`.trim(),
                      placeholder: 'Organisation',
                      'aria-label': 'Organisation',
                    }}
                  />
                  {errors.organisation && (
                    <span className={styles.drawerError} role="alert">
                      {(errors.organisation.message as string) || 'Organisation is required'}
                    </span>
                  )}
                </div>
                <div className={styles.drawerField}>
                  <Input
                    name="industry"
                    register={register}
                    rest={{
                      disabled: isLoad,
                      className:
                        `${styles.drawerInput} ${errors.industry ? styles.drawerInputError : ''}`.trim(),
                      placeholder: 'Industry',
                      'aria-label': 'Industry',
                    }}
                  />
                  {errors.industry && (
                    <span className={styles.drawerError} role="alert">
                      {(errors.industry.message as string) || 'Industry is required'}
                    </span>
                  )}
                </div>
                <Checkbox
                  checkColor="#fff"
                  className={styles.drawerCheckbox}
                  checkboxClassName={styles.drawerCheckboxCheckbox}
                  id="report-privacy-checkbox"
                  isError={!!errors.policyCheckbox}
                  register={register}
                  required
                  name="policyCheckbox"
                  label={
                    <span>
                      Submitting this form confirms your agreement to{' '}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer">
                        Urus Advisory Privacy Policy
                      </a>{' '}
                      and{' '}
                      <a href="/cookies" target="_blank" rel="noopener noreferrer">
                        Cookies Policy
                      </a>
                      .
                    </span>
                  }
                />
                <button
                  type="submit"
                  className={styles.drawerSubmit}
                  disabled={isLoad}
                  onClick={() =>
                    pushToGTM({ event: 'popup_download_click', tagName: 'Pop-up Download' })
                  }
                >
                  {isLoad ? (
                    <TailSpin
                      height="24"
                      width="24"
                      color="#fff"
                      ariaLabel="loading"
                      wrapperClass={styles.drawerLoader}
                    />
                  ) : (
                    'DOWNLOAD'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </Modal>

      <SuccessModal isOpen={isSuccessOpen} handleModal={setIsSuccessOpen} />
    </>
  )
}

export default ReportDownloadModal
